<template>
  <v-container v-if="loadingData" fill-height fluid>
    <v-row justify="center" align="center">
      <v-progress-circular indeterminate :size="60" color="primary" />
    </v-row>
  </v-container>

  <v-container v-else-if="APIError" fill-height fluid>
    <v-row justify="center">
      <v-col cols="12" md="8" lg="6">
        <v-alert border="left" colored-border type="error" elevation="2">
          Sorry, something went wrong.
        </v-alert>
      </v-col>
    </v-row>
  </v-container>

  <v-container v-else fluid>
    <v-row>
      <v-col cols="12">
        <v-card>
          <v-card-title>Create New Invoice</v-card-title>
          <v-card-text>
            <v-form ref="form" v-model="valid" lazy-validation>
              <v-text-field
                v-model="formData['testEmailAddress']"
                id="testEmailAddress"
                label="Test Email"
                name="testEmailAddress"
                hint="The created invoice will be send only to this email address"
                persistent-hint
                prepend-icon="mdi-email-outline"
                :error-messages="formErrors['testEmailAddress']"
                :required="true"
              ></v-text-field>
              <v-divider class="my-5"></v-divider>
              <h4>Package info</h4>
              <v-autocomplete
                v-model="formData['client']"
                :error-messages="formErrors['client']"
                :items="clients"
                item-text="name"
                item-value="id"
                id="client"
                name="client"
                label="Client"
                prepend-icon="mdi-account"
                auto-select-first
              ></v-autocomplete>
              <v-menu
                v-model="issueDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="formData['issueDate']"
                    label="Invoice Issue Date"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                  ></v-text-field>
                </template>
                <v-date-picker
                  v-model="formData['issueDate']"
                  @input="issueDateMenu = false"
                ></v-date-picker>
              </v-menu>
              <v-row align="center">
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="formData['packageQuantity']"
                    id="packageQuantity"
                    label="Packages"
                    name="packageQuantity"
                    prepend-icon="mdi-package-variant-closed"
                    :error-messages="formErrors['packageQuantity']"
                    :required="true"
                    type="number"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="formData['palletQuantity']"
                    id="palletQuantity"
                    label="Pallets"
                    name="palletQuantity"
                    prepend-icon="mdi-shipping-pallet"
                    :error-messages="formErrors['palletQuantity']"
                    :required="true"
                    type="number"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-row align="center">
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="formData['packageVolume']"
                    id="packageVolume"
                    label="Volume"
                    name="packageVolume"
                    suffix="c.f."
                    prepend-icon="mdi-ruler-square"
                    :error-messages="formErrors['packageVolume']"
                    :required="true"
                    type="number"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="formData['packageWeight']"
                    id="packageWeight"
                    label="Weight"
                    name="packageWeight"
                    prepend-icon="mdi-weight-pound"
                    suffix="lb"
                    :error-messages="formErrors['packageWeight']"
                    :required="true"
                    type="number"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-text-field
                v-model="formData['packageValue']"
                id="packageValue"
                label="Declared Value"
                name="packageValue"
                prepend-icon="mdi-cash"
                :error-messages="formErrors['packageValue']"
                :required="true"
                type="number"
              ></v-text-field>
              <h4>Fees</h4>
              <v-text-field
                v-model="formData['customsFee']"
                id="customsFee"
                label="Customs Fee"
                name="customsFee"
                prepend-icon="mdi-currency-usd"
                :error-messages="formErrors['customsFee']"
                :required="true"
                type="number"
              ></v-text-field>
              <v-text-field
                v-model="freightCharge"
                id="freightCharge"
                label="Freight Charge"
                name="freightCharge"
                prepend-icon="mdi-currency-usd"
                :error-messages="formErrors['freightCharge']"
                type="number"
                disabled
              ></v-text-field>
              <v-text-field
                v-model="miamiWarehouseFee"
                id="miamiWarehouseFee"
                label="Miami Warehouse Fee"
                name="miamiWarehouseFee"
                prepend-icon="mdi-currency-usd"
                :error-messages="formErrors['miamiWarehouseFee']"
                type="number"
                disabled
              ></v-text-field>
              <v-text-field
                v-model="customBrokerageFee"
                id="customBrokerageFee"
                label="Custom Brokerage Fee"
                name="customBrokerageFee"
                prepend-icon="mdi-currency-usd"
                :error-messages="formErrors['customBrokerageFee']"
                type="number"
                disabled
              ></v-text-field>
              <v-text-field
                v-model="portFee"
                id="portFee"
                label="Port Fee"
                name="portFee"
                prepend-icon="mdi-currency-usd"
                :error-messages="formErrors['portFee']"
                type="number"
                disabled
              ></v-text-field>
              <v-text-field
                v-model="insurance"
                id="insurance"
                label="Insurance"
                name="insurance"
                prepend-icon="mdi-currency-usd"
                :error-messages="formErrors['insurance']"
                type="number"
                disabled
              ></v-text-field>
              <h4>Additional options</h4>
              <v-text-field
                v-model="formData['miamiAreaDelivery']"
                id="miamiAreaDelivery"
                label="Delivery"
                name="miamiAreaDelivery"
                prepend-icon="mdi-currency-usd"
                :error-messages="formErrors['miamiAreaDelivery']"
                type="number"
              ></v-text-field>
              <v-row
                v-for="(additionalOption, index) in formData[
                  'additionalOptions'
                ]"
                :key="index"
              >
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="formData['additionalOptions'][index]['label']"
                    label="Name"
                    prepend-icon="mdi-format-text"
                    type="text"
                  ></v-text-field>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-text-field
                    v-model="formData['additionalOptions'][index]['value']"
                    label="Value"
                    prepend-icon="mdi-currency-usd"
                    type="number"
                  ></v-text-field>
                </v-col>
              </v-row>
              <v-btn class="ml-5 mb-5" small text @click="addAdditionalOption"
                ><v-icon left> mdi-plus </v-icon>Add More</v-btn
              >
              <h4>Total</h4>
              <v-text-field
                v-model="formData['discount']"
                id="discount"
                label="Discount"
                name="discount"
                prepend-icon="mdi-currency-usd-off"
                :error-messages="formErrors['discount']"
                :required="true"
                type="number"
              ></v-text-field>
              <v-text-field
                v-model="invoiceTotal"
                id="invoiceTotal"
                label="Invoice Total"
                name="invoiceTotal"
                prepend-icon="mdi-currency-usd"
                :error-messages="formErrors['invoiceTotal']"
                type="number"
                disabled
              ></v-text-field>
            </v-form>
          </v-card-text>
          <v-card-actions>
            <v-btn
              color="primary"
              @click="createInvoice"
              :loading="isWaitingForResponse"
              >Create Invoice</v-btn
            >
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <v-snackbar v-model="formSuccessSnackbar" color="success" timeout="2000">
      Invoice successfully created!
      <template v-slot:action="{ attrs }">
        <v-btn
          color="white"
          text
          v-bind="attrs"
          @click="formSuccessSnackbar = false"
        >
          Close
        </v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script>
import axios from "axios";
import { DateTime } from 'luxon';

export default {
  name: 'PickaserviceInvoiceForm',

  data: () => ({
    formSuccessSnackbar: false,
    isWaitingForResponse: false,
    loadingData: true,
    APIError: false,
    clients: null,
    valid: true,
    issueDateMenu: false,
    formData: {
      "testEmailAddress": "richard.christian@pickaservice.ky",
      "issueDate": DateTime.now().setZone("EST").toISODate(),
      "additionalOptions": [],
    },
    formErrors: {},
  }),

  mounted() {
    this.getClients()
  },

  computed: {
    currentDateTime: function () {
      return DateTime.now().setZone("EST").toISODate()
    },
    freightCharge: function () {
      let packageVolume = this.formData["packageVolume"]
      if (!packageVolume) {
        return null
      }
      const priceBreakdowns = [
        { maxVolume: 1, charge: 15.0 },
        { maxVolume: 2, charge: 25.0 },
        { maxVolume: 5, charge: 35.0 },
        { maxVolume: 8, charge: 45.0 },
        { maxVolume: 10, charge: 55.0 },
      ]
      for (const priceBreakdown of priceBreakdowns) {
        if (packageVolume <= priceBreakdown.maxVolume) {
          return this.bankersRound(priceBreakdown.charge)
        }
      }
      let additionalVolume = Math.ceil(packageVolume - 10)
      let totalCharge = 55.0 + 5 * additionalVolume
      return this.bankersRound(totalCharge)
    },
    miamiWarehouseFee: function () {
      const priceList = {
        "package": 2.5,
        "pallet": 15,
      }
      let numberOfPackages = this.formData["packageQuantity"] || 0
      let numberOfPallets = this.formData["palletQuantity"] || 0
      let costOfPackages = numberOfPackages * priceList["package"]
      let costOfPallets = numberOfPallets * priceList["pallet"]
      let warehouseFee = costOfPackages + costOfPallets
      return this.bankersRound(warehouseFee)
    },
    customBrokerageFee: function () {
      let packageValue = this.formData["packageValue"]
      let packageVolume = this.formData["packageVolume"]
      if (!packageValue || !packageVolume) {
        return null
      }
      let brokerageFee = 50
      if (packageValue > 5000) {
        brokerageFee = 100
      } else if (packageValue > 1000) {
        brokerageFee = 75
      } else if (packageVolume < 1) {
        brokerageFee = 0.0
      } else if (packageVolume <= 2) {
        brokerageFee = 25
      }
      return this.bankersRound(brokerageFee)
    },
    portFee: function () {
      let packageVolume = this.formData["packageVolume"]
      if (!packageVolume) {
        return null
      }
      let fee = 0.0
      if (packageVolume >= 1) {
        const portFeeRate = 1.25
        const minPortCharge = 7.80
        fee = portFeeRate * packageVolume
        fee = Math.max(minPortCharge, fee)
      }
      return this.bankersRound(fee)
    },
    insurance: function () {
      let packageValue = this.formData["packageValue"]
      if (!packageValue) {
        return null
      }
      const insuranceRate = 0.02
      const minInsuranceCost = 5.00
      let insuranceCost = insuranceRate * packageValue
      insuranceCost = Math.max(minInsuranceCost, insuranceCost)
      return this.bankersRound(insuranceCost)
    },
    consolidatedGovernmentFees: function () {
      let customsFee = this.formData["customsFee"]
      if (!customsFee || !this.portFee || !this.insurance) {
        return null
      }
      let governmentFees = (
        parseFloat(customsFee) +
        parseFloat(this.portFee) +
        parseFloat(this.insurance)
      )
      return this.bankersRound(governmentFees)
    },
    dueDate: function () {
      let issueDate = this.formData["issueDate"]
      if (!issueDate) {
        return null
      }
      issueDate = DateTime.fromISO(issueDate)
      let calculatedDueDate = issueDate.plus({ days: 7 })
      return calculatedDueDate.toISODate()
    },
    additionalFees: function () {
      let allAdditionalFees = 0
      for (const additionalOption of this.formData["additionalOptions"]) {
        let additionalFee = parseFloat(additionalOption.value)
        if (additionalFee) {
          allAdditionalFees += additionalFee
        }
      }
      return allAdditionalFees
    },
    invoiceTotal: function () {
      let miamiAreaDelivery = this.formData["miamiAreaDelivery"] || 0
      let discount = this.formData["discount"] || 0
      let allFees = [
        this.freightCharge,
        this.miamiWarehouseFee,
        this.customBrokerageFee,
        this.consolidatedGovernmentFees,
        miamiAreaDelivery,
      ]
      if (allFees.some(fee => fee == null || fee.length === 0)) {
        return null
      }
      let total = allFees.reduce(
        (partialSum, fee) => partialSum + parseFloat(fee), 0
      )
      total += this.additionalFees
      total -= parseFloat(discount)
      return this.bankersRound(total)
    },
  },

  methods: {
    getClients: function () {
      this.loadingData = true
      this.APIError = false
      axios
        .get(
          `${process.env.VUE_APP_API_ROOT_URL}clients/`,
          this.$store.getters.axiosConfig
        )
        .then(response => {
          this.clients = response.data
          this.loadingData = false
        })
        .catch(() => {
          this.APIError = true
          this.loadingData = false
        });
    },
    getFormattedDate: function (ISOString) {
      return DateTime.fromISO(ISOString).toFormat("dd-MMM-yyyy")
    },
    bankersRound: function (num) {
      // https://stackoverflow.com/questions/3108986/
      var d = 2;
      var m = Math.pow(10, d);
      var n = +(d ? num * m : num).toFixed(8); // Avoid rounding errors
      var i = Math.floor(n), f = n - i;
      var e = 1e-8; // Allow for rounding errors in f
      var r = (f > 0.5 - e && f < 0.5 + e) ?
        ((i % 2 == 0) ? i : i + 1) : Math.round(n);
      var result = d ? r / m : r
      return result.toFixed(2);
    },
    createInvoice: function () {
      this.isWaitingForResponse = true
      this.APIError = false
      let formData = Object.assign({}, this.formData)
      formData["dueDate"] = this.dueDate
      formData["freightCharge"] = this.freightCharge
      formData["miamiWarehouseFee"] = this.miamiWarehouseFee
      formData["customBrokerageFee"] = this.customBrokerageFee
      formData["portFee"] = this.portFee
      formData["insurance"] = this.insurance
      formData["consolidatedGovernmentFees"] = this.consolidatedGovernmentFees
      formData["invoiceTotal"] = this.invoiceTotal
      axios
        .post(
          `${process.env.VUE_APP_API_ROOT_URL}pickaservice-invoices/`,
          formData,
          this.$store.getters.axiosConfig
        )
        .then(() => {
          this.resetFormData()
          this.formErrors = {}
          this.formSuccessSnackbar = true
          window.scrollTo(0, 0)
          this.isWaitingForResponse = false
        })
        .catch((error) => {
          this.formErrors = error.response.data
          this.isWaitingForResponse = false
        });
    },
    addAdditionalOption: function () {
      this.formData["additionalOptions"].push({ label: "", value: "" })
    },
    resetFormData: function () {
      this.formData = {
        "testEmailAddress": "richard.christian@pickaservice.ky",
        "issueDate": DateTime.now().setZone("EST").toISODate(),
        "additionalOptions": [],
      }
    }
  },
}
</script>

<style lang="scss" scoped>
</style>